import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  BUTTON_VARIANTS,
  IconOrder,
  CardWrapper,
  CardSubtitle,
  CardTitle,
  IconDelete,
  IconButton,
  IconClose,
  Column,
  Row,
  Loader,
  DialogOverlay,
} from 'cdk-radial';
import './MyCart.scss';
import { deleteApiDetails } from '../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from '../../../constants/amplitudeConstants';
import { getOrgAmplitudeProperties,sendAmplitudeData } from '../../../utils/amplitude';
import { Constants, RESOURCE_TYPES } from '../../../constants/constants';
import { setScreen } from '../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../config/uiFactory.config';
import { ScreenPaths } from '../../../constants/constants';
import {
  getPlanName,
  parsePrice,
  getPlanType,
  PLAN_TYPE,
  formatCurrency,
} from '../utils/apiPricingUtil';
import { setAPIDirectoryPage } from '../../../redux/connectedComponent/apiDirectory/apiDirectory.slice';
import { API_DIRECTORY_PAGE } from '../../../constants/APIDirectoryConstants';
import { trackCheckoutStarted, trackRemoveFromCart } from '../../../utils/gtm';

const { API_DIRECTORY, HOME } = ScreenPaths;

const MyCart = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const state = useSelector((state) => state);
  const { fortellisEnterpriseEntityId, authenticationToken: token } =
    state?.loginDetailsSlice;
  const {
    fetchingCartDetails,
    requestedAPIsToRemoveFromCart,
    cart = [],
  } = state?.orderAPISlice;
  const [myCart, setMyCart] = useState(cart);
  const path = window.location?.pathname?.replace(/\/$/, '');
  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice
  );
  useEffect(() => {
    if (path === `/dp/${API_DIRECTORY}` || path === `/dp/${HOME}`) {
      const apis = [
        ...(state?.apiDirectorySlice?.apis || []),
        ...(state?.apiDirectorySlice?.asyncApis || []),
      ];
      const bundles = state?.apiDirectorySlice?.bundles || [];
      const cartApiDetails = cart
        .map((cartItem) => {
          if (cartItem.resourceType === RESOURCE_TYPES.BUNDLE) {
            const matchingBundle = bundles.find(
              (bundleItem) => bundleItem.id === cartItem.resourceId
            );
            if (matchingBundle) {
              const enrichedApis =
                matchingBundle.apis?.map((bundleApi) => {
                  const fullApiDetails = apis.find(
                    (api) => api.id === bundleApi.id
                  );
                  return {
                    ...bundleApi,
                    name: fullApiDetails?.name || bundleApi.name,
                    orgName: fullApiDetails?.orgName || '',
                    description: fullApiDetails?.description || '',
                    visibility: fullApiDetails?.visibility || '',
                  };
                }) || [];
              return {
                ...cartItem,
                details: { ...matchingBundle, apis: enrichedApis },
              };
            }
            return null;
          } else {
            const matchingApi = apis.find(
              (apiItem) => apiItem.id === cartItem.resourceId
            );
            return matchingApi
              ? { ...cartItem, details: { ...matchingApi } }
              : null;
          }
        })
        .filter((item) => item !== null);
      setMyCart(cartApiDetails);
    } else {
      setMyCart(cart);
    }
  }, [
    path,
    cart,
    state?.apiDirectorySlice?.apis,
    state?.apiDirectorySlice?.asyncApis,
    state?.apiDirectorySlice?.bundles,
  ]);

  const handleClose = () => {
    props.setShowCart(false);
  };

  
  const handleDeleteCard = async (resourceId) => {
    const item = myCart.find(api => api.resourceId === resourceId);
    try {
      await dispatch(
        deleteApiDetails({ entityId: fortellisEnterpriseEntityId, token, resourceIds: resourceId })
      ).unwrap();
      
      // Only track on successful deletion
      if (item) {
        trackRemoveFromCart(item, myCart);
      }
    } catch (error) {
      // Error is handled by Redux
    }
  };
  const integrateApis = () => {
    const bundleItemsCount = myCart.filter((x) => x.resourceType?.toLowerCase() === RESOURCE_TYPES.BUNDLE).length;
    const apiItemsCount = myCart.length - bundleItemsCount;
    trackCheckoutStarted(apiItemsCount, bundleItemsCount);

    props.setShowCart(false);
    history.push('/dp/checkout');
    sendAmplitudeData(
      AMPLITUDE_EVENTS.REVIEW_AND_PURCHASE,
      orgAmplitudeProperties
    );
    dispatch(setScreen({ screen: screens.CHECKOUT }));
  };
  const browseApis = () => {
    props.setShowCart(false);
    dispatch(setAPIDirectoryPage({ page: API_DIRECTORY_PAGE.HOME, props: {} }));
  };

  const getPlanFrequencyText = (plan) => {
    return getPlanName(getPlanType(plan)).toLocaleLowerCase() ===
      PLAN_TYPE.SUBSCRIPTION
      ? '/ location per month'
      : '/ month';
  };

  return (
    <DialogOverlay>
      <div className="my-cart">
        <div className="cart-title">
          <IconOrder height={24} width={24} className="icon-order" />{' '}
          {Constants.MY_CART}
          {!!myCart?.length && <> ({myCart.length}) </>}
          <span className="icon-close">
            <IconButton
              data-testid="close-button"
              icon={<IconClose />}
              onClick={handleClose}
            />
          </span>
        </div>
        <Row>
          <Column>
            <div className="border-div"></div>
          </Column>
        </Row>
        {fetchingCartDetails ? (
          <div className="loader">
            <Loader label="fetching cart info..." />
          </div>
        ) : (
          <div className="api-cards">
            {!!myCart?.length ? (
              myCart.map((api) => {
                const hasPricingPlan =
                  api?.resourcePricingPlanId &&
                  api?.resourcePricingPlanId !== 'null';
                const selectedMatchedPlan = api?.details?.plans?.find(
                  (plan) => plan?.id === api?.resourcePricingPlanId,
                );
                const isBundle =
                  api?.resourceType === RESOURCE_TYPES.BUNDLE;
                const showInstallFeeBreakdown =
                  isBundle && selectedMatchedPlan?.installFee != null;
                const planFrequencyLabel = selectedMatchedPlan
                  ? getPlanFrequencyText(selectedMatchedPlan)
                      .replace('/', '')
                      .trim()
                      .replace(/\b\w/g, (char) => char.toUpperCase())
                  : '';
                const planNameDisplay = selectedMatchedPlan?.name || selectedMatchedPlan?.plan_name || 'Dealer Plan';
                const dealerPlanLabel =
                  `${planNameDisplay} - ${planFrequencyLabel}`;

                return (
                  <React.Fragment key={`${api?.resourceId || 'resource'}-${api?.resourcePricingPlanId || 'no-plan'}`}>
                    <div className="card-view">
                      <CardWrapper className="card-wrapper">
                        <div className={`card-details-container ${api?.resourcePricingPlanId && api?.resourcePricingPlanId !== 'null' ? 'card-with-pricing' : 'card-without-pricing'}`}>
                          <div className="card-header">
                            <div className="text-container">
                              <CardTitle className="text-wrap">
                                {api?.resourceType === 'bundle' ? (
                                  <>
                                    {api?.details?.name
                                      ? `${api.details.name} - `
                                      : 'N/A'}
                                    <span className="bundle-badge">Bundle</span>
                                  </>
                                ) : (
                                  api?.details?.name || 'N/A'
                                )}
                              </CardTitle>
                              {api?.resourceType === 'bundle' ? (
                              <CardSubtitle className="text-wrap">
                                  {`${api?.details?.apis?.length} APIs included`} 
                              </CardSubtitle>
                              ) : (
                              <CardSubtitle className="text-wrap">
                                  By {api?.details?.orgName || 'N/A'}
                              </CardSubtitle>
                              )}
                            </div>
                            {requestedAPIsToRemoveFromCart?.includes(api.resourceId)
                            ?<div className="delete-icon"><Loader/></div>
                            : <div className="delete-icon">
                                <IconButton
                                  data-testid="delete-button"
                                  icon={<IconDelete />}
                                  onClick={() => handleDeleteCard(api.resourceId)}
                                />
                              </div>
                            }
                          </div>
                          {hasPricingPlan && !showInstallFeeBreakdown && (
                            <div className="cart-border" />
                          )}
                          {hasPricingPlan && selectedMatchedPlan && (
                            <React.Fragment
                              key={selectedMatchedPlan?.id || 'matched-plan'}
                            >
                              {showInstallFeeBreakdown ? (
                                <div className="cart-plan-breakdown">
                                  <div className="cart-plan-line-item">
                                    <span className="cart-plan-line-item-label">
                                      {dealerPlanLabel}
                                    </span>
                                    <span className="cart-plan-line-item-value">
                                      {formatCurrency(
                                        parsePrice(selectedMatchedPlan),
                                      )}
                                    </span>
                                  </div>
                                  <div className="cart-plan-divider" />
                                  <div className="cart-plan-line-item">
                                    <span className="cart-plan-line-item-label">
                                      Install Fee
                                    </span>
                                    <span className="cart-plan-line-item-value">
                                      {formatCurrency(
                                        selectedMatchedPlan?.installFee,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="cart-plan-details">
                                  <span className="cart-plan-price">
                                    $
                                    {new Intl.NumberFormat('en-US').format(
                                      parsePrice(selectedMatchedPlan),
                                    )}
                                  </span>
                                  <span>
                                    {' '}
                                    {getPlanFrequencyText(selectedMatchedPlan)} -{' '}
                                  </span>
                                  &nbsp;
                                  <span className="cart-plan-type">
                                    {getPlanName(
                                      getPlanType(
                                        selectedMatchedPlan,
                                        isBundle,
                                      ),
                                      isBundle,
                                      selectedMatchedPlan?.name || selectedMatchedPlan?.plan_name
                                    )}{' '}
                                    Plan
                                  </span>
                                </div>
                              )}
                            </React.Fragment>
                          )}
                        </div>
                      </CardWrapper>
                    </div>
                  </React.Fragment>
                );
              })
            ) : (
              <div className="empty-cart">
                <p>{Constants.NO_APIS_IN_CART}</p>
                <div>
                  <Button
                    onClick={browseApis}
                    className="empty-card"
                    text={Constants.BROWSE_API_DIRECTORY}
                    variant={BUTTON_VARIANTS.PRIMARY}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {!!myCart?.length && (
          <div>
            <Row>
              <Column>
                <div className="border-div"></div>
              </Column>
            </Row>
            <div className="apis-footer">
              <Button
                onClick={integrateApis}
                className="card-view footer-btn"
                text={Constants.REVIEW_PURCHASE_CART}
                variant={BUTTON_VARIANTS.PRIMARY}
              />
            </div>
          </div>
        )}
      </div>
    </DialogOverlay>
  );
};

export default MyCart;
